/* =========================================================================
   In-section sub-filters

   Some rails ask one small question of their own — "What's your usual
   size?" on Fashion This Week, "What does your skin usually do?" on The 4
   Thing Routine. Answering one used to do nothing but open the big quiz.
   Now it curates the rail it was asked in, and only that rail:

     answer  ->  the question card slides out
             ->  that section's cards narrow to what suits the answer
             ->  a "Retake" button appears at the right-hand end of
                 the section's heading row, on the same line as the heading
             ->  the answer is remembered (per shopper, see
                 assets/vro-personalization.js) so it still holds next visit

     Retake -> the card slides back, the rail reopens, the button goes.

   No new section is created for these; nothing is fetched. The section
   already rendered its cards with the store's structured data on them, so
   the whole thing is local, instant and reversible.

   The size rail's actual filtering still belongs to assets/custom.js — it
   also re-points each (+) button at the right variant, which is its job and
   stays its job. This file never re-implements that: it reads the state
   custom.js leaves behind (aria-pressed) and drives it by clicking the
   controls custom.js already listens to. Both handlers run, neither knows
   about the other, and removing either leaves the other working.
   ========================================================================= */
(function () {
  'use strict';

  if (window.VroSubfilter) return;

  /* The same single circular arrow the quiz-cta-banner's Retake button
     uses, so every "take it again" control in the theme reads as one
     thing. Was a two-arrow refresh glyph, which said "swap" rather than
     "start again". */
  var RETAKE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>';

  /* ---------------------------------------------------------------
     What the row says once it has been answered.

     A rail that quietly drops two cards looks like a rail that did
     nothing — especially on a tab where every product happens to carry the
     chosen size and nothing needs dropping at all. So the subheading stops
     being a fixed line and starts reporting the answer: what was applied,
     and how much of the row survived it.

     The lines rotate, so answering twice does not print the same sentence
     twice, and {count} / {total} / {value} are filled from what is actually
     on screen in the tab being looked at. Retake puts the original line
     back, verbatim.
  --------------------------------------------------------------- */
  var SUBLINES = {
    /* "What are you working towards" — a goal, so the row talks about what
       the products are for rather than what the shopper is. */
    goal: {
      some: [
        '{count} of {total} are for {value} — those are the ones showing',
        'Filtered to what helps with {value}. {count} still standing',
        'Kept the {count} that work for {value}'
      ],
      all: [
        'All {count} of these already help with {value}',
        'Every pick here is for {value}'
      ],
      none: [
        'Nothing in this row is for {value} — retake to see them all',
        'No picks for {value} in this row. Retake to bring it back'
      ],
      rebuilt: [
        'Rebuilt for {value} — {count} picks, closest first',
        '{count} for {value}, best match first then the rest',
        'Rebuilt around {value}. Closest first, keep scrolling'
      ],
      na: [
        'This row is not chosen by goal, so nothing was changed',
        'Goal does not separate this row — showing it as it was'
      ]
    },
    body: {
      some: [
        '{count} of {total} are cut for {value} — those are the ones showing',
        'Filtered to the cuts that suit {value}. {count} still standing',
        'Kept the {count} that hang right on {value}'
      ],
      all: [
        'All {count} of these are already cut for {value}',
        'Every pick here suits {value}'
      ],
      none: [
        'Nothing in this row is cut for {value} — retake to see them all',
        'No {value} cuts in this row. Retake to bring it back'
      ],
      rebuilt: [
        'Rebuilt for {value} — {count} picks, best fit first',
        '{count} cut for {value}, closest fit first then the rest',
        'Rebuilt around {value}. Best fits first, keep scrolling'
      ],
      na: [
        'This row is not cut by fit, so nothing was changed',
        'Fit does not separate this row — showing it as it was'
      ]
    },
    size: {
      some: [
        '{count} of {total} come in {value} — those are the ones showing',
        'Your size, sorted: {count} picks in {value}',
        'Filtered to {value}. {count} still standing',
        'Only what you can actually buy in {value} — {count} picks'
      ],
      all: [
        'Good news — all {count} of these come in {value}',
        'Every pick here is available in {value}',
        'All {count} in stock in {value}, nothing to rule out'
      ],
      none: [
        'Nothing in this row comes in {value} — retake to see them all',
        'No {value} in this row yet. Retake to bring it back'
      ],
      rebuilt: [
        'Rebuilt around {value} — {count} that fit, closest match first',
        '{count} picks in {value}, best first then everything else that fits',
        'Rebuilt for {value}. Best matches first, keep scrolling for the rest'
      ],
      na: [
        'This row is not sized in {value} — showing it as it was',
        '{value} does not apply here, so nothing was changed'
      ]
    },
    skin: {
      some: [
        '{count} picks for skin that {value}',
        'Narrowed to what suits skin that {value} — {count} of {total}',
        '{count} of {total} here suit skin that {value}',
        'Filtered for skin that {value}: {count} picks'
      ],
      all: [
        'All {count} of these suit skin that {value}',
        'Every pick here works for skin that {value}'
      ],
      none: [
        'Nothing here is made for skin that {value} — retake to see everything',
        'No match for skin that {value} in this row. Retake to bring it all back'
      ],
      rebuilt: [
        'Rebuilt for skin that {value} — best matches first',
        'Rebuilt around skin that {value}: {count} picks, closest first',
        '{count} picks for skin that {value}, best first then the near misses'
      ],
      na: [
        'Nothing in this row is chosen by skin type — showing it as it was',
        'Skin type does not change this row, so nothing was touched'
      ]
    },
    hair: {
      some: [
        '{count} picks for hair that {value}',
        'Narrowed to what suits hair that {value} — {count} of {total}',
        '{count} of {total} here suit hair that {value}'
      ],
      all: [
        'All {count} of these suit hair that {value}',
        'Every pick here works for hair that {value}'
      ],
      none: [
        'Nothing here is made for hair that {value} — retake to see everything'
      ],
      rebuilt: [
        'Rebuilt for hair that {value} — best matches first',
        'Rebuilt around hair that {value}: {count} picks, closest first'
      ],
      na: [
        'Nothing in this row is chosen by hair type — showing it as it was'
      ]
    }
  };

  /* Sizes are labels and stay exactly as the merchant wrote them ("M",
     "UK-9", "32"). The skin and hair chips are clauses written for a
     shopper ("Gets oily by noon"), so they get folded into the sentence
     instead — lowercased, and reworded where the chip is a noun phrase
     that would not read after "skin that". */
  var VALUE_PHRASES = {
    'a bit of both': 'sits between oily and dry',
    "i'm not sure": 'you have not pinned down yet'
  };

  function phraseFor(value, facet) {
    if (!value) return '';
    /* A body type is a proper noun on the card ("Trapezoid"), and reads as
       one in a sentence too — "cut for a Trapezoid build". */
    if (facet === 'size' || facet === 'body') return String(value);
    /* "Building muscle" -> "building muscle", which is what reads right
       after "are for" and after "helps with". */
    if (facet === 'goal') return String(value).charAt(0).toLowerCase() + String(value).slice(1);
    var key = String(value).toLowerCase().trim();
    if (VALUE_PHRASES[key]) return VALUE_PHRASES[key];
    return value.charAt(0).toLowerCase() + value.slice(1);
  }

  /* ---------------------------------------------------------------
     The confirmation pill.

     In the reference the question card does not simply vanish — it is
     replaced, in the same slot, by a short acknowledgement: a blue tick,
     "Noted — oily by noon", and one line saying what actually changed. It
     reuses the row's existing insight pill (.info-pill on a phone,
     .feed-rail-header-pill on web), so it is the same shape, the same
     gradient and the same "4 from 120" count sitting on the right; only
     the words change, and they change back on retake.
  --------------------------------------------------------------- */
  var CHECK_ICON =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M7.4 12.7 10.1 15.4 16.6 7.9" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* The chip wording, shortened to fit a headline. */
  var SHORT_ANSWERS = {
    'gets oily by noon': 'oily by noon',
    'feels tight and dry': 'tight and dry',
    'a bit of both': 'a bit of both',
    'reacts to most things': 'reacts easily'
  };

  var NOTED_DETAIL = {
    goal: {
      some: 'Kept the {count} that help with that and hid the rest.',
      all: 'Everything in this row already helps with that.',
      none: 'Nothing here is for that yet — retake to bring it all back.',
      rebuilt: 'Pulled in {count} that help with that — closest first, the rest follow.',
      capped: 'Pulled in {count} that help with that — closest first, View All for the rest.',
      na: 'This row is not chosen by goal, so it is unchanged.'
    },
    body: {
      some: 'Kept the {count} cut for that build and hid the rest.',
      all: 'Everything in this row already suits that build.',
      none: 'Nothing here is cut for that yet — retake to bring it all back.',
      rebuilt: 'Pulled in {count} that hang right on that build — best fit first, the rest follow.',
      capped: 'Pulled in {count} that hang right on that build — best fit first, View All for the rest.',
      na: 'This row is not chosen by fit, so it is unchanged.'
    },
    size: {
      some: 'Kept the {count} that come in {value} and hid the rest.',
      all: 'Everything in this row already comes in {value}.',
      none: 'Nothing in this row comes in {value} — retake to bring it all back.',
      rebuilt: 'Pulled in {count} you can buy in {value} — best fit first, the rest follow.',
      capped: 'Pulled in {count} you can buy in {value} — best fit first, View All for the rest.',
      na: 'This row is not sized that way, so it is unchanged.'
    },
    skin: {
      some: 'Kept {count} that suit it and dropped the rest.',
      all: 'Everything in this row already suits it.',
      none: 'Nothing here is made for that yet — retake to bring it all back.',
      rebuilt: 'Swapped in {count} that suit it, closest match first — scroll for the rest.',
      capped: 'Swapped in {count} that suit it, closest match first — View All for the rest.',
      na: 'Nothing in this row is chosen by that, so it is unchanged.'
    },
    hair: {
      some: 'Kept {count} that suit it and dropped the rest.',
      all: 'Everything in this row already suits it.',
      none: 'Nothing here is made for that yet — retake to bring it all back.',
      rebuilt: 'Swapped in {count} that suit it, closest match first — scroll for the rest.',
      capped: 'Swapped in {count} that suit it, closest match first — View All for the rest.',
      na: 'Nothing in this row is chosen by that, so it is unchanged.'
    }
  };

  /* What the bar says in the moment between the tap and the rebuilt row —
     the reference clip's "Rebuilding your rack". Written once, so the bar
     has its final shape (tick, headline, line, Retake) from the first
     frame and only the words of this line change after. */
  var NOTED_PENDING = 'Rebuilding your rack\u2026';

  function shortAnswer(value, facet) {
    if (!value) return '';
    if (facet === 'size') return 'size ' + value;
    if (facet === 'body') return String(value).toLowerCase() + ' build';
    if (facet === 'goal') return phraseFor(value, facet);
    return SHORT_ANSWERS[String(value).toLowerCase().trim()] || phraseFor(value, facet);
  }

  function api() { return window.VroPersonalization || null; }

  /* assets/vro-motion.js — preloading, height morphs and text fades. Every
     call below falls back to the old instant behaviour when it is absent. */
  function motion() { return window.VroMotion || null; }
  function morph(el, mutate, ms) {
    var m = motion();
    var scope = el && el.closest ? el.closest('[data-vro-facet]') : null;
    /* A remembered answer put back on page load is not something the
       shopper did just now, so it lands in place without moving. */
    if (m && el && !(scope && scope.__vroQuiet)) m.morphHeight(el, mutate, { duration: ms || 420 });
    else if (mutate) mutate();
  }

  function scopes() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-vro-facet]'));
  }

  function facetOf(scope) { return scope.getAttribute('data-vro-facet'); }

  /* A row that answers only for itself.

     Sharing an answer is right when one question is asked twice on a page —
     the skin question above a rail and again as its own card, where
     answering one and leaving the other still asking would be odd. It is
     wrong when several rows each ask it about their own shelf: four rails
     on the home page ask what your skin does, and answering one was
     answering all four.

     A section opts out by stamping data-vro-facet-solo with something
     unique to it. Such a row neither broadcasts its answer nor takes
     anyone else's, and remembers its own under its own key. */
  /* Answering for itself is the resting state, so it does not depend on an
     attribute being present to be true. Four rails on one page ask what
     your skin usually does, each about its own shelf; an answer given to
     one is not an answer to the others, and reading it back to all four on
     the next visit was the same mistake twice. A row opts INTO sharing
     with data-vro-facet-share, for the case where the same question really
     is being asked in two places.

     The id comes from the section that wraps the scope when the scope does
     not name one itself — Shopify gives every section a stable id, so the
     key survives a reload, and two copies of one section on a page still
     get a key each. */
  function soloOf(scope) {
    if (scope.hasAttribute('data-vro-facet-share')) return '';
    var named = scope.getAttribute('data-vro-facet-solo');
    if (named) return named;
    var section = scope.closest('[id^="shopify-section-"]');
    if (section) return section.id;
    /* No section wrapper — a rendered fragment, or a test. The scope's own
       id will do, and where there is not one either, sharing is the only
       honest answer left rather than a key every scope would collide on. */
    return scope.id || '';
  }

  /* Where this row's answer is remembered. A shared row stores under the
     facet, so every row asking that question reads the same value back; a
     solo row adds its own id, so a reload brings each row back to what it
     was told rather than to what the last row answered. */
  function facetKey(scope) {
    var solo = soloOf(scope);
    return solo ? facetOf(scope) + '@' + solo : facetOf(scope);
  }
  function typeFieldOf(scope) { return scope.getAttribute('data-vro-facet-type') || 'skin'; }
  function reselectLabel(scope) {
    return scope.getAttribute('data-vro-facet-reselect') || 'Retake';
  }

  /* ---------------------------------------------------------------
     The question card
  --------------------------------------------------------------- */
  function cards(scope) {
    return Array.prototype.slice.call(scope.querySelectorAll('[data-vro-facet-card]'));
  }

  /* The card leaves and is then taken out of the flow. The finishing step
     is cancellable: a shopper who answers and immediately hits "Retake"
     would otherwise get the card back and then watch a stale timer hide it
     again a moment later. */
  function settle(card) {
    if (card.__vroFacetTimer) { clearTimeout(card.__vroFacetTimer); card.__vroFacetTimer = null; }
    if (card.__vroFacetListening && card.__vroFacetDone) {
      card.__vroFacetListening.removeEventListener('transitionend', card.__vroFacetDone);
    }
    card.__vroFacetDone = null;
    card.__vroFacetListening = null;
  }

  /* Anything an older roll-up left on a wrapper, taken off so the question
     is laid out exactly as it first was. */
  function unroll(card) {
    var host = card.__vroRollHost;
    if (!host) return;
    host.classList.remove('vro-facet-roll');
    host.style.height = '';
    card.__vroRollHost = null;
  }

  /* The wrapper above a rail owns the gap to the products, and an empty
     wrapper still spends it, so it goes and comes back with the card. */
  function wrapperOf(card) {
    var wrap = card.parentElement;
    return wrap && (wrap.classList.contains('rail-quiz-above') ||
                    wrap.hasAttribute('data-vro-facet-roll')) ? wrap : null;
  }

  /* No roll up, no roll down: the question goes (and comes back) in one
     frame and the section's height eases to fit, the way sort-a-concern's
     card answers (VroMotion.settle). A remembered answer put back on load
     (animate false) lands in place without moving. */
  function swapQuestion(scope, animate, change) {
    var m = motion();
    if (animate && m && m.settle && !scope.__vroQuiet) m.settle(scope, change);
    else change();
  }

  function hideQuestion(scope, animate) {
    swapQuestion(scope, animate, function () {
      cards(scope).forEach(function (card) {
        settle(card);
        unroll(card);
        card.classList.remove('vro-facet-card--leaving', 'vro-facet-card--arriving');
        card.classList.add('vro-facet-card--hidden');
        var wrap = wrapperOf(card);
        if (wrap) wrap.classList.add('vro-facet-card--hidden');
      });
    });
  }

  function showQuestion(scope) {
    swapQuestion(scope, true, function () {
      cards(scope).forEach(function (card) {
        settle(card);
        unroll(card);
        card.classList.remove('vro-facet-card--hidden', 'vro-facet-card--leaving', 'vro-facet-card--arriving');
        /* Both kinds of wrapper the load-time restore hides. Only the home
           page's was ever put back, so a listing page's question, hidden on
           load, never returned on Retake. */
        var wrap = wrapperOf(card);
        if (wrap) wrap.classList.remove('vro-facet-card--hidden');
      });
    });
  }

  /* ---------------------------------------------------------------
     The "Retake" button, mounted in the section's heading row
  --------------------------------------------------------------- */
  /* One button per row, owned by the row rather than by whatever container
     it currently sits in — applyNoted moves it into the "Noted ..." bar,
     which is rebuilt from scratch on every answer, so a button looked up
     from the DOM would be recreated over and over. Held here instead, and
     re-placed wherever it needs to be. */
  function mountFor(scope) {
    var btn = scope.__vroRetakeBtn;
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'vro-reselect';
      btn.setAttribute('data-vro-reselect', '');
      btn.innerHTML = RETAKE_ICON + '<span></span>';
      scope.__vroRetakeBtn = btn;
    }
    btn.querySelector('span').textContent = reselectLabel(scope);

    /* Homed in the heading row until there is a Noted bar to live in —
       and back there if the bar goes away. A row without either mount
       keeps the button, it just has nowhere to show it yet. */
    if (!btn.parentNode) {
      var mount = scope.querySelector('[data-vro-reselect-mount]');
      if (mount) mount.appendChild(btn);
    }
    return btn;
  }

  function setReselect(scope, on) {
    var btn = mountFor(scope);
    if (!btn) return;
    if (on) {
      btn.hidden = false;
      /* A beat behind the question's fold, so the two do not cross: the
         card is on its way out for ~.58s, and a button fading in over the
         top of it reads as both happening by accident. */
      setTimeout(function () { btn.classList.add('is-visible'); }, 260);
    } else {
      btn.classList.remove('is-visible');
      btn.hidden = true;
    }
  }

  /* ---------------------------------------------------------------
     Narrowing the rails of one section

     Cards the answer rules out are hidden, cards it has nothing to say
     about are kept — unless something in the rail genuinely matches, in
     which case the matches are the answer and the rest step aside. A rail
     left with nothing says so rather than sitting empty.
  --------------------------------------------------------------- */
  /* The home page's rails are .rail-scroll inside a Bootstrap .tab-pane,
     and that is the default. The collection page draws the same kind of
     row with its own classes, so a scope can name its own — which is all
     it takes for this whole file to work there, rather than a second copy
     of it written against different selectors. */
  function selOf(scope, attr, fallback) {
    return scope.getAttribute(attr) || fallback;
  }

  function railsOf(scope) {
    return Array.prototype.slice.call(
      scope.querySelectorAll(selOf(scope, 'data-vro-facet-rails', '.rail-scroll')));
  }

  /* Only what the shopper can currently see. A tabbed rail has one row on
     screen and four behind it, and reporting "12 of 40" for a row showing
     five products would be worse than saying nothing. */
  function visibleRails(scope) {
    var pane = selOf(scope, 'data-vro-facet-pane', '.tab-pane.active');
    var rails = selOf(scope, 'data-vro-facet-rails', '.rail-scroll');
    var active = scope.querySelectorAll(pane + ' ' + rails);
    return active.length ? Array.prototype.slice.call(active) : railsOf(scope);
  }

  function tally(scope) {
    var total = 0, shown = 0, scoped = 0;
    visibleRails(scope).forEach(function (rail) {
      if (rail.__vroOutOfScope) return;
      scoped++;
      Array.prototype.forEach.call(rail.querySelectorAll('.feed-card'), function (card) {
        total++;
        if (!card.hidden && !card.classList.contains('vro-facet-hidden')) shown++;
      });
    });
    return { total: total, shown: shown, scoped: scoped };
  }

  /* Is the row the shopper is looking at one that stops short and ends on
     the View All tile? Only the rails on screen: a tabbed section's other
     four are behind this one and say nothing about what is being read. */
  function endsOnViewAll(scope) {
    var rails = visibleRails(scope);
    for (var i = 0; i < rails.length; i++) {
      if (rails[i].classList.contains('vro-rail-capped') && viewAllShown(rails[i])) return true;
    }
    return false;
  }

  function emptyNote(rail, show, message) {
    var note = rail.querySelector('.vro-facet-empty');
    if (!show) {
      if (note) note.remove();
      return;
    }
    if (!note) {
      note = document.createElement('p');
      note.className = 'vro-facet-empty';
      rail.appendChild(note);
    }
    note.textContent = message;
  }

  function subheadingOf(scope) {
    /* Weather rails rewrite their own subheading from the forecast; leave
       those alone rather than have two scripts fight over one line. */
    var el = scope.querySelector(
      selOf(scope, 'data-vro-facet-subheading', '.feed-rail-header .rail-subheading'));
    if (!el || el.hasAttribute('data-vro-weather-text')) return null;
    if (!('__vroSubOriginal' in el)) el.__vroSubOriginal = el.textContent;
    return el;
  }

  function fill(template, value, counts, facet) {
    return template
      .replace(/\{value\}/g, phraseFor(value, facet))
      .replace(/\{count\}/g, counts.shown)
      .replace(/\{total\}/g, counts.total);
  }

  function writeSubheading(el, text) {
    clearTimeout(el.__vroSubTimer);
    if (el.textContent === text && !el.classList.contains('vro-subline-swap')) return;
    el.classList.add('vro-subline-swap');
    el.__vroSubTimer = setTimeout(function () {
      /* A one-line sentence becoming a two-line one grows the header, and
         everything under it used to drop by that line in one frame. */
      morph(el, function () { el.textContent = text; }, 320);
      el.classList.remove('vro-subline-swap');
    }, 200);
  }

  function pillsOf(scope) {
    return Array.prototype.slice.call(scope.querySelectorAll(
      selOf(scope, 'data-vro-facet-pill', '.info-pill, .feed-rail-header-pill')));
  }

  /* Back to the merchant's own copy — for a row whose answer has been
     cleared, and for a pill this scope has decided not to report in. */
  function restorePill(scope, pill) {
    if (!pill.classList.contains('vro-noted')) return;
    /* Restoring the merchant's own markup takes the button with it, so
       detach it first and let mountFor re-home it. */
    var parked = scope.__vroRetakeBtn;
    if (parked && parked.parentNode && pill.contains(parked)) {
      parked.parentNode.removeChild(parked);
      parked.classList.remove('vro-reselect--noted');
    }
    pill.innerHTML = pill.__vroPillOriginal;
    pill.classList.remove('vro-noted', 'vro-noted-pill');
    mountFor(scope);
  }

  /* Which pill reports the answer.

     A tabbed rail carries two: one beside the heading, and one inside each
     tab above the products. Promoting every pill put the same bar on the
     page twice, one under the other — the heading's and the open tab's.

     The heading's wins: it belongs to the row rather than to one tab of it,
     and it is the one that stays put while the tabs are switched. The tabs
     keep the merchant's own copy, which is what they say before the
     question is answered.

     A row with no header pill still reports in the pill it has. Those are
     one per tab and only the open tab is on screen, so they are never two
     at once either. */
  /* A pill the shopper can actually see. The header pill is hidden below
     992px — it belongs to a heading row that only exists on a wide screen —
     and a hidden element is still an element, so preferring it by class
     alone promoted the invisible one and left the tab's own pill saying
     what it said before the question was answered. Which is how a phone
     came to show two bars: a "Noted" one it had no room for, and the
     merchant's line underneath still describing an unfiltered row. */
  function isDrawn(el) {
    if (!el) return false;
    /* Computed style, not a measured box. getClientRects() answers "is this
       laid out right now", which is a different question and the wrong one:
       it comes back empty for an element that is perfectly visible but has
       not been painted yet, so the answer depended on when the shopper
       happened to answer. display is what the stylesheet decided and it
       reads the same before first paint as after. */
    try { return window.getComputedStyle(el).display !== 'none'; }
    catch (e) { return true; }
  }

  function notedPillsOf(scope) {
    /* A header pill this width does not draw is not a candidate at all.

       Preferring only the visible one was half the fix and the wrong half:
       with no visible header pill the list fell back to every pill in the
       scope, and the hidden one was still in it. So it was promoted anyway
       — and promoting it is what made it appear, since .vro-noted-pill
       carries display: grid and lands after the rule that hides it. The
       phone got its two bars back by a longer route.

       Dropped from the candidates instead, so the only pill left to promote
       is the one under the tabs, which is the one a phone can see. */
    var all = [];
    var header = [];
    var pills = pillsOf(scope);
    for (var i = 0; i < pills.length; i++) {
      var pill = pills[i];
      var isHeader = pill.classList.contains('feed-rail-header-pill');
      if (isHeader && !isDrawn(pill)) continue;
      all.push(pill);
      if (isHeader) header.push(pill);
    }
    return header.length ? header : all;
  }

  function applyNoted(scope, value, pending) {
    var facet = facetOf(scope);
    var counts = tally(scope);
    var notedTexts = [];
    var chosen = value ? notedPillsOf(scope) : [];

    pillsOf(scope).forEach(function (pill) {
      if (!('__vroPillOriginal' in pill)) pill.__vroPillOriginal = pill.innerHTML;

      if (chosen.indexOf(pill) === -1) {
        if (pill.classList.contains('vro-noted')) {
          morph(pill, function () { restorePill(scope, pill); });
          pill.classList.add('vro-pill-back');
          setTimeout(function () { pill.classList.remove('vro-pill-back'); }, 450);
        }
        return;
      }

      /* The count on the right is the merchant's own copy and keeps
         counting the same thing, so it is carried over rather than
         rewritten. */
      var count = pill.querySelector('.info-pill-count');
      var detail = (NOTED_DETAIL[facet] || NOTED_DETAIL.skin);
      /* A row the shopper can scroll to the end of is told to scroll; a row
         that stops at six and closes on the tile is told where the rest
         went, since scrolling no longer reaches them. */
      var rebuilt = (endsOnViewAll(scope) && detail.capped) ? detail.capped : detail.rebuilt;
      detail = (counts.scoped === 0 && detail.na) ? detail.na
             : counts.shown === 0 ? detail.none
             : (scope.__vroRebuiltFor === value && rebuilt) ? rebuilt
             : (counts.shown === counts.total ? detail.all : detail.some);
      var detailText = pending ? NOTED_PENDING : fill(detail, value, counts, facet);
      var titleText = 'Noted — ' + shortAnswer(value, facet);

      /* Already a Noted bar: change its words, keep its elements. Rebuilding
         it re-popped the tick and flashed the line on every update — the
         pending line, the result line, each tab switch. */
      var oldTitle = pill.classList.contains('vro-noted') && pill.querySelector('.vro-noted-title');
      var oldLine = oldTitle && pill.querySelector('.vro-noted-detail');
      var oldText = oldTitle && pill.querySelector('.vro-noted-text');
      if (oldTitle && oldLine && oldText) {
        var m = motion();
        if (oldTitle.textContent !== titleText) {
          if (m) m.swapText(oldTitle, titleText); else oldTitle.textContent = titleText;
        }
        if (m) {
          /* The new line may wrap differently; the bar eases to it as the
             words change. */
          m.swapText(oldLine, detailText, function (apply) { morph(pill, apply, 320); });
        } else {
          oldLine.textContent = detailText;
        }
        notedTexts.push(oldText);
        return;
      }

      var fresh = function () {
      pill.innerHTML = '';
      /* vro-noted marks the state; vro-noted-pill is what the stylesheet
         lays a Noted bar out by. A promoted pill keeps whatever element it
         started as — an .info-pill here, a .feed-rail-header-pill there,
         a .clp-curation-why on the listing page, and whatever a section
         names in data-vro-facet-pill next — so without the second class the
         layout has to enumerate them, and misses the one added after it was
         written. assets/vro-personalization.css already carries a rule
         written as .clp-curation-why.vro-noted-pill, which is where this
         was meant to have been all along. */
      pill.classList.add('vro-noted', 'vro-noted-pill');

      var tick = document.createElement('span');
      tick.className = 'vro-noted-check';
      tick.innerHTML = CHECK_ICON;
      pill.appendChild(tick);

      var text = document.createElement('span');
      text.className = 'vro-noted-text';

      var title = document.createElement('span');
      title.className = 'vro-noted-title';
      title.textContent = titleText;
      text.appendChild(title);

      var line = document.createElement('span');
      line.className = 'vro-noted-detail';
      line.textContent = detailText;
      text.appendChild(line);

      /* Retake belongs to the answer, so it sits under it inside this bar
         rather than off in the heading row. It is one button, so which bar
         gets it is decided once, after they are all built — see below. */
      notedTexts.push(text);

      pill.appendChild(text);
      if (count) pill.appendChild(count);
      /* The button goes in now, not after, so the bar is measured at the
         size it will keep and grows to it once — rather than growing for
         the headline and then again for Retake. */
      var early = mountFor(scope);
      if (early && pill.offsetParent !== null) {
        early.classList.add('vro-reselect--noted');
        text.appendChild(early);
      }
      };
      pill.classList.add('vro-noted-enter');
      morph(pill, fresh, 440);
      setTimeout(function () { pill.classList.remove('vro-noted-enter'); }, 500);
    });

    /* A tabbed rail has two bars: one in the section header and one inside
       each tab pane. Handing the button to whichever came first put it in
       the header bar — often scrolled off the top of a phone, and never
       the one the shopper is reading. So it goes to the last bar actually
       on screen (a closed tab pane is display:none, which is no
       offsetParent), and only falls back to the first if none of them can
       be measured yet.

       Measured on the bar, not on the text span the button lands in. That
       span is display: contents — the stylesheet flattens it so the bar's
       grid can place the title, detail and button as its own cells — and
       an element with no box of its own has no offsetParent, on screen or
       not. Measuring it said "nothing on screen" for every bar, and Retake
       went back to the heading row, outside the bar, on every rail and
       every phone. The bar has a box; it is the thing that is or is not
       drawn. */
    if (value) {
      var onScreen = notedTexts.filter(function (t) {
        var bar = t.parentNode;
        return !!bar && bar.offsetParent !== null;
      });
      var retake = mountFor(scope);
      if (retake) {
        if (onScreen.length) {
          retake.classList.add('vro-reselect--noted');
          onScreen[onScreen.length - 1].appendChild(retake);
        } else {
          /* No bar on screen to put it in — either none was promoted or the
             only one that was is the heading pill, which a phone does not
             draw. Handing it to a hidden bar is how the routine rail ended
             up answered with no way back: the question folds away, the rail
             stays narrowed, and the button that undoes it is inside a
             display: none element.

             The heading mount is always drawn, so the button goes back
             there and the shopper can always retake. --noted comes off with
             it: that class styles the button for life inside a bar. */
          retake.classList.remove('vro-reselect--noted');
          var home = scope.querySelector('[data-vro-reselect-mount]');
          if (home && retake.parentNode !== home) home.appendChild(retake);
        }
      }
    }
  }

  function applySubheading(scope, value) {
    var el = subheadingOf(scope);
    if (!el) return;

    if (!value) {
      writeSubheading(el, el.__vroSubOriginal);
      return;
    }

    var facet = facetOf(scope);
    var pools = SUBLINES[facet] || SUBLINES.skin;
    var counts = tally(scope);
    var pool = (counts.scoped === 0 && pools.na) ? pools.na
             : counts.shown === 0 ? pools.none
             : (scope.__vroRebuiltFor === value && pools.rebuilt) ? pools.rebuilt
             : (counts.shown === counts.total ? pools.all : pools.some);

    /* Rotates, so answering twice never prints the same sentence twice. */
    scope.__vroSubIndex = (scope.__vroSubIndex || 0) + 1;
    writeSubheading(el, fill(pool[scope.__vroSubIndex % pool.length], value, counts, facet));
  }

  /* ---------------------------------------------------------------
     Rebuilding a row from its own collection

     Hiding what does not suit can only ever shrink a row — five cards
     become two and the shopper is worse off for answering. The reference
     rebuilds instead: the products that suit the answer come in, whether or
     not they were among the ones first rendered, and the row stays the size
     the merchant set.

     The pool is that tab's own collection, so nothing arrives from outside
     the shelf the merchant chose; only the order and the depth change. The
     cards are the theme's own, from the same snippet, so add-to-cart,
     wishlist and quick-add keep working on them by delegation.

     Everything is reversible: the row's first cards are kept aside and put
     back verbatim on retake. And if the fetch fails, the row is simply
     narrowed the old way — never emptied.
  --------------------------------------------------------------- */
  /* How deep the row goes. The rail is a carousel, so a shopper who
     answered should get the best matches under their thumb and then keep
     scrolling through progressively looser ones — not a row truncated at
     whatever count the merchant set for the un-answered state. Everything
     that does not contradict the answer stays in, ranked; these two
     numbers are only there so a 6,000-product collection cannot put six
     thousand cards in the DOM. */
  var REBUILD_PAGES = 2;              /* 100 candidates fetched per row */
  var REBUILD_MAX = 48;               /* cards actually rendered */

  /* An answered row is a short answer with a way to the rest, not a long
     scroll: five on a phone and six on a desktop, and then the View All
     tile that closes the rail — the sixth cell there, the seventh here.

     Done here rather than by rendering fewer, because a card hidden this
     way comes straight back when the browser is widened or the answer
     cleared, and because :nth-child cannot help: it counts hidden siblings
     too, and this has to count what is actually showing. */
  var MOBILE_SHOWN = 5;
  var DESKTOP_SHOWN = 6;

  function narrowScreen() {
    return window.matchMedia && window.matchMedia('(max-width: 991.98px)').matches;
  }

  function viewAllOf(rail) {
    return rail.querySelector(':scope > .feed-view-all');
  }

  /* Is the tile that closes this row actually drawn at this width? Rails
     that keep the question as their last tile on desktop hide the View All
     there (see the quiz_desktop block in sections/feed-rail.liquid), and
     answering is what swaps the two over.

     The tile's own computed display, not offsetParent: a rail in a closed
     tab has no offset parent at all, and those rails have to be capped to
     the same length as the one on screen or switching tabs would show a
     different row. display is not inherited, so the tile inside a
     display:none tab still reports the display its own rules give it. */
  function viewAllShown(rail) {
    var tile = viewAllOf(rail);
    if (!tile || !window.getComputedStyle) return !!tile;
    return window.getComputedStyle(tile).display !== 'none';
  }

  /* How long this row runs once it has an answer.

     Desktop only caps a row that ends on the tile. A row without one has
     no way to the matches past the cap, so hiding them would lose them —
     better a long scroll than a short row that drops what it found. The
     phone keeps its five either way, which is how it has always been. */
  function capFor(rail, value) {
    if (!value) return Infinity;
    if (narrowScreen()) return MOBILE_SHOWN;
    return viewAllShown(rail) ? DESKTOP_SHOWN : Infinity;
  }

  /* A rank chip is a position in the row, so it has to follow the row. The
     numbers are rendered by Liquid in the merchant's order; answering a
     question reorders and hides cards, which would leave them reading
     02, 05, 01. Rewritten here over whatever is actually showing, and the
     original is kept so clearing the answer puts it back.

     Only rails that already have chips get any: this never adds a number
     to a rail whose markup did not ask for one. */
  function renumberRails(scope) {
    railsOf(scope).forEach(function (rail) {
      var n = 0;
      railCards(rail).forEach(function (card) {
        var chip = card.querySelector('.feed-card-rank');
        if (!chip) return;
        if (!('__vroRank' in chip)) chip.__vroRank = chip.textContent;
        if (card.classList.contains('vro-facet-hidden') ||
            card.classList.contains('vro-facet-capped') || card.hidden) return;
        n += 1;
        chip.textContent = (n < 10 ? '0' : '') + n;
      });
    });
  }

  function capRails(scope, value) {
    railsOf(scope).forEach(function (rail) {
      var cap = capFor(rail, value);
      var shown = 0;
      railCards(rail).forEach(function (card) {
        if (card.classList.contains('vro-facet-hidden') || card.hidden) {
          card.classList.remove('vro-facet-capped');
          return;
        }
        shown += 1;
        card.classList.toggle('vro-facet-capped', shown > cap);
      });

      /* A capped row is a whole shelf rather than the start of one, so it
         says how many cells it came to and lets the stylesheet lay them
         out side by side instead of scrolling. The tile is the last of
         them, which is the +1. */
      var capped = cap !== Infinity;
      rail.classList.toggle('vro-rail-capped', capped);
      if (capped && viewAllShown(rail)) {
        rail.style.setProperty('--vro-capped-cols', String(cap + 1));
      } else {
        rail.style.removeProperty('--vro-capped-cols');
      }
    });
  }

  function railCards(rail) {
    return Array.prototype.slice.call(rail.querySelectorAll(':scope > .feed-card'));
  }

  function snapshot(rail) {
    if (!rail.__vroOriginalCards) rail.__vroOriginalCards = railCards(rail);
    return rail.__vroOriginalCards;
  }

  /* The size rail's (+) buttons point at a specific variant once a size is
     known. custom.js does this for the cards it rendered; these are cards
     we inserted afterwards, which it has never seen, so they are pointed
     the same way here rather than left opening the variant picker for a
     size the shopper has already given us. */
  function pointAtSize(card, size) {
    var addBtn = card.querySelector('.feed-add-btn');
    if (!addBtn) return;
    var pairs = (card.getAttribute('data-size-variants') || '').split('|');
    for (var i = 0; i < pairs.length; i++) {
      var parts = pairs[i].split(':');
      if (parts[0] === size && parts[1]) {
        if (addBtn.hasAttribute('data-quick-add-handle') && !addBtn.hasAttribute('data-orig-quick-add')) {
          addBtn.setAttribute('data-orig-quick-add', addBtn.getAttribute('data-quick-add-handle'));
        }
        addBtn.removeAttribute('data-quick-add-handle');
        addBtn.setAttribute('data-add-variant', parts[1]);
        return;
      }
    }
  }

  /* Only products that genuinely answer the question, ranked strongest
     first. "Best first, then the near misses" means a weaker MATCH — a
     product whose benefits address the answer but which does not declare
     the type — not a product with nothing to do with the question.

     Scoring zero is not a near miss, it is silence, and silence is what
     puts a disposable razor in a row filtered by skin type: every grooming
     product on this store declares "All" skin types, so declaring a type
     proves nothing. What separates them is whether anything they claim
     actually addresses the answer. A Sensi razor says "Post Shave
     Soothing", so it earns its place under "reacts to most things" and
     loses it under "gets oily by noon" — which is right both times. */
  /* body type -> the fits that suit it, from the scope, falling back to
     the mapping the section ships with. */
  var BODY_FITS_FALLBACK = {
    'trapezoid':          ['slim', 'regular'],
    'inverted trapezoid': ['slim', 'regular'],
    'oval':               ['relaxed', 'loose', 'regular'],
    'rectangular':        ['slim', 'regular', 'oversized']
  };

  function fitsFor(scope, value) {
    var key = String(value || '').toLowerCase().trim();
    var attr = scope.getAttribute('data-vro-fits-' + key.replace(/[^a-z0-9]+/g, '-'));
    if (attr) {
      return attr.split(',').map(function (f) { return f.toLowerCase().trim(); }).filter(Boolean);
    }
    return BODY_FITS_FALLBACK[key] || [];
  }

  /* Garments whose Fit metafield is filled in with a word that is not a
     fit. "null" arrives as the literal four characters on this store, the
     same junk the hair metafields carry, and reading it as a cut would rule
     a product out of every body type. */
  var FIT_NOT_A_FIT = ['', 'null', 'none', 'n/a', 'na', '-'];

  function declaredFit(card) {
    var raw = (card.getAttribute('data-vro-fit') || '').toLowerCase().trim();
    return FIT_NOT_A_FIT.indexOf(raw) > -1 ? '' : raw;
  }

  /* The product's own fit decides it. If the garment says what cut it is
     and that cut is not one that suits this build, it is a no — the same
     way a Dry-only cream is a no for oily skin — and not merely an absence
     of a reason to show it.
     
     That distinction is what was letting the wrong things through: a loose
     vest survived "Trapezoid" and a rail of shorts declaring "oversized"
     survived it too, because their titles happen to read "Regular Fit" and
     a zero score only loses to a positive one. Copy cannot outvote the
     field.

     The words in tags and titles are still read, but only where the field
     is empty — a guess is better than nothing, and worse than a fact. */
  function fitScore(card, fits) {
    if (!fits.length) return 0;
    var declared = declaredFit(card);

    if (declared) {
      for (var i = 0; i < fits.length; i++) {
        if (declared.indexOf(fits[i]) > -1) return 6 - i;
      }
      return -1;                       /* it says what it is, and it is not this */
    }

    var text = ((card.getAttribute('data-vro-tags') || '') + ' ' +
                (card.getAttribute('data-vro-title') || '')).toLowerCase();
    for (var j = 0; j < fits.length; j++) {
      if (text.indexOf(fits[j]) > -1) return 3 - j > 0 ? 3 - j : 1;
    }
    return 0;
  }

  function rankFor(scope, cards, value, topic, options) {
    var engine = api();
    var facet = facetOf(scope);
    var ranked = [];
    var gate = !(options && options.ignoreTopic);

    cards.forEach(function (card, i) {
      var score;
      /* Right kind of product for this row before anything else — a row
         about sunscreen does not get a razor, however well the razor
         happens to answer the question. */
      if (gate && topic && topic.length && engine && !engine.matchesTopic(card, topic)) return;
      if (facet === 'size') {
        var sizes = (card.getAttribute('data-sizes') || '').split(',').filter(Boolean);
        if (sizes.indexOf(value) === -1) return;          /* cannot be bought in that size */
        score = 0;
      } else if (facet === 'body') {
        /* Body type is not a field on any product, and inventing one would
           mean the answer did nothing until someone filled it in on
           thousands of items. Fit is the field that already exists and is
           already populated — Regular, Relaxed, Loose, Slim, Oversized —
           and fit is what actually decides whether a garment hangs right
           on a build. The mapping lives on the scope so a merchant can
           tune it without a deploy. */
        score = fitScore(card, fitsFor(scope, value));
        if (score <= 0) return;
      } else {
        score = engine ? engine.facetScore(card, facet, value, typeFieldOf(scope)) : 0;
        if (score <= 0) return;                            /* contradicts it, or says nothing about it */
      }
      ranked.push({ card: card, score: score, order: i });
    });

    ranked.sort(function (a, b) { return b.score - a.score || a.order - b.order; });
    return ranked;
  }

  /* A row whose products cannot answer the question at all — jeans sized
     28-38 when the answer is "M", razors when the answer is a skin type.
     Emptying it would be a lie dressed as a filter, so it is handed back
     exactly as the merchant built it and left out of the counts. */
  function releaseRail(rail, value) {
    rail.__vroOutOfScope = value;
    rail.__vroBuiltFor = null;
    Array.prototype.forEach.call(rail.querySelectorAll(':scope > .feed-card'), function (card) {
      card.classList.remove('vro-facet-hidden');
      card.hidden = false;
    });
    emptyNote(rail, false);
  }

  /* The row dips and comes back rather than snapping to a different set of
     products under the shopper's eyes — the cross-fade the reference clip
     uses. The class goes on before the fetch so the dip covers the wait. */
  /* One card rising into place, a beat after the one before it. The row
     swapping its whole contents in a single frame reads as a glitch even
     when every product in it is right — nothing moves, so the new set
     simply is there, and the shopper cannot tell their answer caused it.

     Capped at eight: past that the stagger stops reading as sequence and
     starts reading as lag, and the ninth card is off the edge of a phone
     anyway. The class is taken off at the end so a later rebuild can run
     the same animation again. */
  var ARRIVE_STEP = 45;
  var ARRIVE_MAX = 8;

  function arrive(card, index) {
    if (!card || !card.classList) return;
    var owner = card.closest ? card.closest('[data-vro-facet]') : null;
    if (owner && owner.__vroQuiet) return;
    card.classList.remove('vro-card-arrive');
    /* The Pop's stagger, shared by every quiz (VroMotion.popDelay). */
    card.style.animationDelay = (window.VroMotion && window.VroMotion.popDelay
      ? window.VroMotion.popDelay(index) : Math.min(index, ARRIVE_MAX) * ARRIVE_STEP) + 'ms';
    /* Read back, so a card being re-animated starts from the beginning
       rather than the browser folding both writes into one paint. */
    void card.offsetWidth;
    card.classList.add('vro-card-arrive');
    var done = function () {
      card.classList.remove('vro-card-arrive');
      card.style.animationDelay = '';
      card.removeEventListener('animationend', done);
    };
    card.addEventListener('animationend', done);
  }

  function fadeOut(rail) { rail.classList.add('vro-rail-swapping'); }
  function fadeIn(rail) {
    /* One frame with the new cards in place, so the fade has something to
       fade back to. */
    requestAnimationFrame(function () { rail.classList.remove('vro-rail-swapping'); });
  }

  /* Where a row's candidates come from.

     Not just the collection the tab points at. A tab labelled "SPF" is
     wired to the razors collection on this store, and "Beard" to the same
     one — so trusting the collection means the row can never show a
     sunscreen no matter how many the store stocks. The label is the honest
     statement of what the row is for, so it is also used to ask the rest of
     the inventory.

     Both halves are recall only. Everything that comes back still has to
     pass the topic gate and the structured scoring, so a wider net does not
     mean a looser row — it means the right products can be found at all. */
  function sourcesFor(rail, topic) {
    var sources = [];
    var handle = rail.getAttribute('data-vro-collection');
    if (handle) sources.push({ collection: handle, maxPages: REBUILD_PAGES });
    if (topic.length) sources.push({ search: topic.slice(0, 3).join(' '), maxPages: 1 });
    return sources;
  }

  function topicOf(rail, scope) {
    var engine = api();
    if (!engine || !engine.topicPhrases) return [];
    var label = rail.getAttribute('data-vro-topic') ||
                (scope && scope.getAttribute('data-vro-topic')) || '';
    return engine.topicPhrases(label);
  }

  function rebuildRail(scope, rail, value) {
    var engine = api();
    var topic = topicOf(rail, scope);
    var sources = engine && engine.pool ? sourcesFor(rail, topic) : [];
    if (!engine || !sources.length || !value) return Promise.resolve(false);

    if (rail.__vroBuiltFor === value) return Promise.resolve(true);

    /* The row dims a little while the new set is fetched and its photos
       decoded — never to blank, and never swapped in half loaded. */
    rail.classList.add('vro-rail-pending');
    var unpend = function () { rail.classList.remove('vro-rail-pending'); };
    return engine.pool(sources).then(function (cards) {
      if (!cards.length) { unpend(); return false; }

      var ranked = rankFor(scope, cards, value, topic).slice(0, REBUILD_MAX);

      /* The topic gate rejected the lot. That is the right answer for a row
         whose products genuinely cannot speak to it — but not for a row the
         merchant wired to a collection whose name this vocabulary simply
         does not know: the "Tanks" tab points at the vests collection and
         "Layers" at rain jackets, and nothing in either carries the tab's
         word, so every product was thrown out and both rows were handed
         back unfiltered. A named collection IS the statement of intent, so
         where one is set the gate stands down rather than the row giving
         up. */
      if (!ranked.length && rail.getAttribute('data-vro-collection')) {
        ranked = rankFor(scope, cards, value, topic, { ignoreTopic: true }).slice(0, REBUILD_MAX);
      }

      if (!ranked.length) {
        unpend();
        restoreRail(rail);          /* whatever a previous answer did, undo */
        releaseRail(rail, value);
        return 'na';
      }

      var m = motion();
      var ready = m ? m.preload(ranked.slice(0, 8).map(function (h) { return h.card; }))
                    : Promise.resolve();
      return ready.then(function () {
      /* A newer answer may have landed while the photos loaded. */
      if (activeValue(scope) !== value) { unpend(); return false; }
      var put = function () {
      rail.__vroOutOfScope = null;
      snapshot(rail);
      railCards(rail).forEach(function (card) { card.remove(); });

      /* Back in front of whatever closes the row — the View All tile, or
         the question card on a row that keeps it last. */
      var tail = rail.querySelector(':scope > .feed-view-all') ||
                 rail.querySelector(':scope > .rail-quiz-card, :scope > .size-quiz-card');
      var n = 0;
      ranked.forEach(function (hit) {
        if (facetOf(scope) === 'size') pointAtSize(hit.card, value);
        hit.card.classList.remove('vro-facet-hidden', 'vro-facet-capped');
        if (tail) rail.insertBefore(hit.card, tail);
        else rail.appendChild(hit.card);
      });

      rail.__vroBuiltFor = value;
      capRails(scope, value);
      renumberRails(scope);
      /* Only the cards actually drawn settle in, in order. */
      railCards(rail).forEach(function (card) {
        if (card.classList.contains('vro-facet-capped') || card.hidden) return;
        arrive(card, n++);
      });
      /* Back to the start, so the best match is the one under their thumb
         rather than wherever they had scrolled to before answering. */
      rail.scrollLeft = 0;
      };
      morph(rail, put, 460);
      unpend();
      if (window.VroWishlist && window.VroWishlist.sync) window.VroWishlist.sync();
      return true;
      });
    }).catch(function () { unpend(); return false; });
  }

  /* The way out of a narrowed row should land somewhere narrowed too.
     assets/vro-plp.js reads this on the collection page and rebuilds the
     grid around the same answer, so "View All" means all of what suits
     you rather than all of everything. The original href is kept so
     clearing the answer puts the tile back exactly as the merchant set
     it. */
  function retargetViewAll(scope, facet, value) {
    railsOf(scope).forEach(function (rail) {
      var tile = rail.querySelector(':scope > .feed-view-all');
      if (!tile) return;
      if (!('__vroViewAllHref' in tile)) tile.__vroViewAllHref = tile.getAttribute('href') || '';
      var base = tile.__vroViewAllHref;
      if (!base) return;

      if (!value) { tile.setAttribute('href', base); return; }

      var clean = base.split('#')[0].split('?')[0];
      var query = base.indexOf('?') > -1 ? base.split('?')[1].split('#')[0] : '';
      var params = new URLSearchParams(query);
      params.set('vro', facet + ':' + value);
      tile.setAttribute('href', clean + '?' + params.toString());
    });
  }

  function restoreRail(rail) {
    if (!rail.__vroOriginalCards) return;
    var originals = rail.__vroOriginalCards;
    var tail = rail.querySelector(':scope > .feed-view-all') ||
               rail.querySelector(':scope > .rail-quiz-card, :scope > .size-quiz-card');
    morph(rail, function () {
      railCards(rail).forEach(function (card) { card.remove(); });
      originals.forEach(function (card) {
        card.hidden = false;
        card.classList.remove('vro-facet-hidden', 'vro-facet-capped');
        if (tail) rail.insertBefore(card, tail);
        else rail.appendChild(card);
      });
      rail.__vroBuiltFor = null;
      rail.scrollLeft = 0;
      /* The merchant's cards come back in the same soft order the answer's
         arrived in. They were loaded before, so nothing paints blank. */
      if (rail.offsetParent !== null) {
        originals.slice(0, ARRIVE_MAX + 1).forEach(function (card, i) { arrive(card, i); });
      }
    }, 460);
    if (window.VroWishlist && window.VroWishlist.sync) window.VroWishlist.sync();
  }

  /* Only the row on screen is rebuilt up front; the tabs behind it are done
     when they are opened, so answering costs one request, not five. */
  function rebuildScope(scope, value, options) {
    options = options || {};
    /* A row can rebuild from its label alone, so a missing or wrong
       collection is no longer a reason to skip it. */
    function buildable(rail) {
      return rail.getAttribute('data-vro-collection') || rail.getAttribute('data-vro-topic');
    }
    var all = railsOf(scope).filter(buildable);
    var visible = visibleRails(scope).filter(buildable);
    var rails = options.rails || (options.all ? all : visible);
    if (!rails.length) return Promise.resolve(false);

    if (!value) {
      all.forEach(function (rail) { restoreRail(rail); rail.__vroOutOfScope = null; });
      scope.__vroRebuiltFor = null;
      return Promise.resolve(false);
    }

    return Promise.all(rails.map(function (rail) {
      return rebuildRail(scope, rail, value);
    })).then(function (results) {
      var changed = results.some(function (r) { return r === true; });
      /* The background pass works on rows nobody is looking at: it must not
         overwrite what the visible row decided, nor re-write the heading a
         second time after one tap. */
      var background = !!options.rails;
      if (!background) scope.__vroRebuiltFor = changed ? value : null;

      /* Every tab gets personalised, not just the one on screen. The
         visible row is done first so the answer is instant; the rest go
         out straight after, so they are ready before anyone switches to
         them and nothing depends on the tab event firing. */
      if (!options.rails && !options.all) {
        var rest = all.filter(function (rail) { return visible.indexOf(rail) === -1; });
        if (rest.length) {
          setTimeout(function () {
            rebuildScope(scope, value, { rails: rest }).catch(function () {});
          }, 0);
        }
      }

      if (changed && !background) {
        /* The rebuilt row is already only what suits, but re-running the
           narrowing keeps one code path deciding what is visible. */
        filterScope(scope, value);
        applyNoted(scope, value);
        applySubheading(scope, value);
      }
      return changed;
    });
  }

  /* Will answering this row rebuild it from its collection? The same test
     rebuildScope makes, asked up front. */
  function canRebuild(scope) {
    var engine = api();
    if (!engine || !engine.pool) return false;
    return visibleRails(scope).some(function (rail) {
      return rail.getAttribute('data-vro-collection') || rail.getAttribute('data-vro-topic');
    });
  }

  function filterScope(scope, value) {
    var engine = api();
    var facet = facetOf(scope);

    /* custom.js owns which cards a size hides, and re-points their (+)
       buttons at the right variant — none of that is re-implemented here.
       What it does not do is say anything when a row is left with nothing,
       which happens the moment a shopper picks M on a tab whose products
       are sized 28-38 or UK-7. So we read the result and speak for it. */
    if (facet === 'size') {
      railsOf(scope).forEach(function (rail) {
        if (rail.__vroBuiltFor === value) { emptyNote(rail, false); return; }
        if (rail.__vroOutOfScope === value) { releaseRail(rail, value); return; }
        var list = Array.prototype.slice.call(rail.querySelectorAll('.feed-card'));
        if (!list.length) return;
        var shown = list.filter(function (card) { return !card.hidden; }).length;
        emptyNote(rail, !!value && shown === 0,
          'Nothing here comes in ' + (value || 'that size') + '.');
      });
      return;
    }

    railsOf(scope).forEach(function (rail) {
      /* A row that rebuilt itself from its collection has already decided
         what belongs in it, in order. Narrowing it again would hide the
         products the ranking deliberately kept to fill the row. */
      if (rail.__vroBuiltFor === value) { emptyNote(rail, false); return; }
      if (rail.__vroOutOfScope === value) { releaseRail(rail, value); return; }

      var list = Array.prototype.slice.call(rail.querySelectorAll('.feed-card'));
      if (!list.length) return;

      /* Body type needs no engine — it reads the garment's cut off the
         card. Every other facet is the engine's judgement. */
      if (!value || (!engine && facet !== 'body')) {
        list.forEach(function (card) { card.classList.remove('vro-facet-hidden'); });
        emptyNote(rail, false);
        return;
      }

      var bodyFits = facet === 'body' ? fitsFor(scope, value) : null;
      var scores = list.map(function (card) {
        if (bodyFits) return fitScore(card, bodyFits);
        return engine.facetScore(card, facet, value, typeFieldOf(scope));
      });
      var anyPositive = scores.some(function (s) { return s > 0; });

      var visible = 0;
      list.forEach(function (card, i) {
        var hide = scores[i] < 0 || (anyPositive && scores[i] <= 0);
        card.classList.toggle('vro-facet-hidden', hide);
        if (!hide) visible++;
      });

      emptyNote(rail, visible === 0, facet === 'body'
        ? 'Nothing in this row is cut for that yet — retake to see everything.'
        : 'Nothing in this row suits that yet — retake to see everything.');
    });
  }

  /* ---------------------------------------------------------------
     Reading and driving the current answer
  --------------------------------------------------------------- */
  function activeValue(scope) {
    if (facetOf(scope) === 'size') {
      var pressed = scope.querySelector('.size-quiz-btn[aria-pressed="true"]');
      return pressed ? pressed.getAttribute('data-size-value') : null;
    }
    var chip = scope.querySelector('[data-vro-facet-option].is-selected');
    return chip ? chip.getAttribute('data-vro-facet-option') : null;
  }

  function markChips(scope, value) {
    var chips = scope.querySelectorAll('[data-vro-facet-option]');
    Array.prototype.forEach.call(chips, function (chip) {
      var on = value !== null && chip.getAttribute('data-vro-facet-option') === value;
      chip.classList.toggle('is-selected', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /* Applies what is currently chosen to one section's chrome and cards. */
  function sync(scope, options) {
    options = options || {};
    var value = activeValue(scope);

    /* First, so everything below reads the state the stylesheet is already
       showing: a rail that keeps the question as its last tile swaps it for
       the View All tile on this class, and whether that tile is drawn is
       what decides how long the row runs. */
    scope.classList.toggle('vro-facet-answered', !!value);

    if (value) {
      hideQuestion(scope, options.animate !== false);
      setReselect(scope, true);
    } else {
      showQuestion(scope);
      setReselect(scope, false);
    }

    var quiet = options.animate === false;
    scope.__vroQuiet = quiet;
    var token = (scope.__vroSyncToken || 0) + 1;
    scope.__vroSyncToken = token;

    /* One answer, one change on screen.

       A row that is about to rebuild itself used to be narrowed first —
       five cards dropping to three in a frame — and then rebuilt a moment
       later with a different set, so every answer reached the shopper as
       two jumps, the Noted bar and the heading were written twice, and the
       new cards landed before their photos. Now the row keeps what it is
       showing (dimmed) until the rebuilt set is ready, and the bar says
       "Rebuilding your rack" in the meantime. Only a row that cannot
       rebuild is narrowed in place, as before. */
    var rebuilding = !!value && canRebuild(scope);

    if (!rebuilding) {
      filterScope(scope, value);
      capRails(scope, value);
      renumberRails(scope);
      applyNoted(scope, value);
      applySubheading(scope, value);
    } else {
      applyNoted(scope, value, true);
    }
    retargetViewAll(scope, facetOf(scope), value);

    /* Then, if this row can rebuild itself, it does — and re-reports. */
    if (value) {
      /* Nothing awaits this, so it swallows its own failures: a rebuild
         that cannot happen leaves the row narrowed, which is a worse
         answer but still an answer — and never a red line in the console
         for a shopper to screenshot. */
      var settleQuiet = function () { if (scope.__vroSyncToken === token) scope.__vroQuiet = false; };
      rebuildScope(scope, value).then(function (changed) {
        if (rebuilding && !changed && scope.__vroSyncToken === token && activeValue(scope) === value) {
          filterScope(scope, value);
          capRails(scope, value);
          renumberRails(scope);
          applyNoted(scope, value);
          applySubheading(scope, value);
        }
        settleQuiet();
      }).catch(function () {
        if (rebuilding && scope.__vroSyncToken === token && activeValue(scope) === value) {
          filterScope(scope, value);
          capRails(scope, value);
          applyNoted(scope, value);
          applySubheading(scope, value);
        }
        settleQuiet();
      });
    } else {
      railsOf(scope).forEach(restoreRail);
      scope.__vroRebuiltFor = null;
      scope.__vroQuiet = false;
    }

    if (options.persist !== false) {
      var engine = api();
      if (engine) engine.profile.setFacet(facetKey(scope), value);
    }
    return value;
  }

  /* One question, one answer, however many places ask it.

     On a phone the skin question appears twice — once above the rail and
     once as its own teaser card — and answering it in one place while the
     other still asks would be odd. So the answer is carried to every other
     section asking the same thing, which curates them and collapses their
     cards too. Sizes are left alone: assets/custom.js owns that rail and
     only one section has one. */
  /* Who else hears an answer. Two rows are in the same conversation when
     they name the same group — which is what soloOf returns: '' for a row
     that shares with everyone asking this question, and the group's name,
     or the section's own id, for a row that does not.

     Comparing the two names rather than testing "is either one solo"
     covers all three cases with one line. Both shared: '' === '', they
     sync, as before. Two ordinary rows: each falls back to its own
     section id, the names differ, and answering one is not answering the
     other — the four skin questions on the home page stay independent.
     And a rail and the quiz card beneath it, both stamped with the same
     group, now sync: the question sits outside the row it curates and
     still curates it. facetKey already keys storage by that same name, so
     a reload brings the pair back together. */
  function propagate(facet, value, origin) {
    if (facet === 'size') return;
    var group = origin ? soloOf(origin) : '';
    scopes().forEach(function (other) {
      if (other === origin || facetOf(other) !== facet) return;
      if (soloOf(other) !== group) return;
      markChips(other, value);
      sync(other, { animate: true, persist: false });
    });
  }

  function clearScope(scope) {
    if (facetOf(scope) === 'size') {
      /* Let custom.js undo its own filter and restore the (+) buttons. */
      var reset = scope.querySelector('[data-size-reset]');
      var pressed = scope.querySelector('.size-quiz-btn[aria-pressed="true"]');
      if (reset) reset.click();
      else if (pressed) pressed.click();
      /* The click path re-enters sync(); make sure the chrome settles even
         if neither control is present. */
      if (!reset && !pressed) sync(scope);
      return;
    }
    markChips(scope, null);
    sync(scope);
    propagate(facetOf(scope), null, scope);
  }

  /* Puts a remembered answer back, driving the same controls a shopper
     would have used so every other listener sees the same events. */
  function restore(scope) {
    var engine = api();
    if (!engine) return;
    var saved = engine.profile.getFacet(facetKey(scope));
    if (!saved) return;

    /* A guest's next visit, the card asks again. The answer is still on
       the profile and still shapes what the rails show; what it stops
       doing is settling the card, so a returning shopper meets the
       question rather than yesterday's Noted bar. A signed-in shopper's
       card stays answered, visit after visit, until they retake it (see
       isFresh in assets/vro-personalization.js). Size is a memory, not a
       question of the day, and is put back however old it is. */
    if (facetOf(scope) !== 'size' && engine.profile.isFresh && !engine.profile.isFresh(facetKey(scope))) return;

    if (facetOf(scope) === 'size') {
      var buttons = scope.querySelectorAll('.size-quiz-btn');
      for (var i = 0; i < buttons.length; i++) {
        if (buttons[i].getAttribute('data-size-value') === saved) {
          if (buttons[i].getAttribute('aria-pressed') !== 'true') buttons[i].click();
          break;
        }
      }
      /* Even where the card is not rendered (the size card is desktop-only)
         the answer still holds, so the button has to be there to undo it. */
      sync(scope, { animate: false, persist: false });
      return;
    }

    markChips(scope, saved);
    sync(scope, { animate: false, persist: false });
  }

  /* ---------------------------------------------------------------
     Events — all delegated on document, so cards, chips and whole
     sections that arrive later (tab panes, AJAX, the theme editor)
     work without re-binding anything.
  --------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var scope = e.target.closest ? e.target.closest('[data-vro-facet]') : null;

    var reselect = e.target.closest && e.target.closest('[data-vro-reselect]');
    if (reselect && scope) {
      e.preventDefault();
      clearScope(scope);
      return;
    }

    if (!scope) return;

    var chip = e.target.closest('[data-vro-facet-option]');
    if (chip) {
      e.preventDefault();
      var value = chip.getAttribute('data-vro-facet-option');
      markChips(scope, activeValue(scope) === value ? null : value);
      var applied = sync(scope);
      propagate(facetOf(scope), applied, scope);
      return;
    }

    /* Size buttons belong to custom.js. This runs after its handler
       (it is bound on the rail, we are on document), so aria-pressed is
       already up to date by the time we read it. */
    if (e.target.closest('.size-quiz-btn') || e.target.closest('[data-size-reset]')) {
      sync(scope);
    }
  });

  /* A tabbed rail shows one row at a time, so the count in the subheading
     has to follow whichever tab is open. Bootstrap's tab event bubbles, so
     one listener covers every rail on the page. */
  document.addEventListener('shown.bs.tab', function (e) {
    var scope = e.target && e.target.closest ? e.target.closest('[data-vro-facet]') : null;
    if (!scope) return;
    var value = activeValue(scope);
    if (!value) return;
    rebuildScope(scope, value).then(function (changed) {
      if (changed) return;                 /* rebuildScope already reported */
      filterScope(scope, value);
      applyNoted(scope, value);
      applySubheading(scope, value);
    }).catch(function () { /* the row keeps whatever it was showing */ });
  });

  function initAll(fromRestore) {
    scopes().forEach(function (scope) {
      if (scope.__vroFacetReady) return;
      scope.__vroFacetReady = true;
      mountFor(scope);
      setReselect(scope, false);
      if (fromRestore !== false) restore(scope);
    });
  }

  /* The cap is a viewport decision, so it has to survive a rotate or a
     window drag across the breakpoint. Only the cap is recomputed — not
     the whole row, which would refetch a pool for a resize. */
  var capTimer = null;
  window.addEventListener('resize', function () {
    if (capTimer) clearTimeout(capTimer);
    capTimer = setTimeout(function () {
      scopes().forEach(function (scope) { capRails(scope, activeValue(scope)); });
    }, 150);
  });

  function refreshAll() {
    scopes().forEach(function (scope) { sync(scope, { animate: false, persist: false }); });
  }

  window.VroSubfilter = { init: initAll, sync: sync, refresh: refreshAll, clear: clearScope };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Sections re-rendered by the theme editor or by any AJAX swap come back
     without their chrome; give them theirs again. */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target || document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-vro-facet]'), function (scope) {
      scope.__vroFacetReady = false;
    });
    initAll();
  });

  if (window.MutationObserver) {
    var pending = null;
    var observer = new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () { initAll(); }, 120);
    });
    observer.observe(document.body || document.documentElement, { childList: true, subtree: true });
  }
})();
